import React, { useState, useEffect } from 'react';
import { Building2, Users, Shield, Tag, Eye, EyeOff, FileText, Map, ExternalLink, Loader } from 'lucide-react';
import { useAuth } from '../../../../contexts/AuthContext';
import { useAlert } from '../../../../components/AlertProvider';
import api from '../../../../services/api';
import gammeConfigService, { PaymentTerm } from '../../../../services/gammeConfigService';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
// import { PhoneInput } from '@/components/ui/PhoneInput';
import { Client, Admin } from './types';
import { EmailActionModal } from '../../../../components/clients/EmailActionModal';

interface ClientEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  client: Client | null;
  onSuccess: (updatedClient: Client) => void;
}

export const ClientEditModal: React.FC<ClientEditModalProps> = ({ isOpen, onClose, client, onSuccess }) => {
  const { user } = useAuth();
  const { showError } = useAlert();

  const buildAddressComplete = (address: string, postalCode: string, city: string) => {
    return [address, postalCode, city]
      .map((v) => (typeof v === 'string' ? v.trim() : ''))
      .filter(Boolean)
      .join(' ');
  };

  const buildMapsLink = (addressComplete: string) => {
    const trimmed = addressComplete.trim();
    return trimmed ? `https://www.google.com/maps/search/${encodeURIComponent(trimmed)}` : '';
  };

  const buildMapsEmbedSrc = (addressComplete: string) => {
    const trimmed = addressComplete.trim();
    return trimmed
      ? `https://www.google.com/maps?q=${encodeURIComponent(trimmed)}&output=embed`
      : '';
  };

  const [formStep, setFormStep] = useState<number>(1);
  const [saving, setSaving] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showMapPreview, setShowMapPreview] = useState<boolean>(false);
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [adminsLoading, setAdminsLoading] = useState<boolean>(false);
  const [centralBuyers, setCentralBuyers] = useState<Client[]>([]);
  const [loadingCentrals, setLoadingCentrals] = useState<boolean>(false);
  const [showEmailModal, setShowEmailModal] = useState<boolean>(false);
  const [paymentTermsList, setPaymentTermsList] = useState<PaymentTerm[]>([]);
  const brandOptions = ['NOVA FLORA', 'DOUCE FRANCE'] as const;
  const defaultBrand = brandOptions[0];

  const [form, setForm] = useState<{
    company_name: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    address: string;
    city: string;
    postal_code: string;
    country: string;
    maps_link: string;
    siret: string;
    website: string;
    password: string;
    is_active: boolean;
    status: 'prospect' | 'actif' | 'inactif' | 'bloque' | 'ferme';
    client_type: 'pharmacie' | 'parapharmacie' | 'centrale' | 'magasin_bio' | 'therapeute' | 'member' | 'independent' | 'central';
    marque: string;
    parent_buyer_id: number | null;
    admin_id: number | null;
    payment_mode: string;
    payment_terms: string;
    invoice_email: string;
    central_purchasing_id: number | null;
    use_central_purchasing_bpu: boolean;
    pappers_url: string;
    infogreffe_url: string;
    pharmanity_url: string;
    rdv_status: 'R0' | 'R1' | 'R2' | 'R3' | '';
    cote: 'Glacier' | 'Froid' | 'Tiède' | 'Chaud' | '';
  }>({
    company_name: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'France',
    maps_link: '',
    siret: '',
    website: '',
    password: '',
    is_active: true,
    status: 'prospect',
    client_type: 'pharmacie',
    marque: defaultBrand,
    parent_buyer_id: null,
    admin_id: null,
    payment_mode: 'Virement',
    payment_terms: '30 jours FDM',
    invoice_email: '',
    central_purchasing_id: null,
    use_central_purchasing_bpu: true,
    pappers_url: '',
    infogreffe_url: '',
    pharmanity_url: '',
    rdv_status: '',
    cote: '',
  });

  useEffect(() => {
    gammeConfigService.listPaymentTerms().then(setPaymentTermsList).catch(console.error);
  }, []);

  useEffect(() => {
    if (isOpen && client) {
      setFormStep(1);
      setShowMapPreview(false);
      setForm({
        company_name: client.company_name,
        first_name: client.first_name || '',
        last_name: client.last_name || '',
        email: client.email,
        phone: client.phone || '',
        address: client.address || '',
        city: client.city || '',
        postal_code: client.postal_code || '',
        country: client.country || 'France',
        maps_link: client.maps_link || '',
        siret: client.siret || '',
        website: client.website || '',
        password: '',
        is_active: client.is_active,
        status: client.status || 'prospect',
        client_type: (client.client_type === 'central' ? 'centrale' : client.client_type) as any,
        marque: client.marque || defaultBrand,
        parent_buyer_id: client.parent_buyer_id || null,
        admin_id: client.admin_id,
        payment_mode: client.payment_mode || 'Virement',
        payment_terms: client.payment_terms || (paymentTermsList.find(t => t.is_default)?.value ?? '30 jours FDM'),
        invoice_email: client.invoice_email || '',
        central_purchasing_id: client.central_purchasing_id || null,
        use_central_purchasing_bpu: client.use_central_purchasing_bpu ?? true,
        pappers_url: client.pappers_url || '',
        infogreffe_url: client.infogreffe_url || '',
        pharmanity_url: client.pharmanity_url || '',
        rdv_status: client.rdv_status || '',
        cote: client.cote || '',
      });
    }
  }, [isOpen, client, paymentTermsList]);

  const loadAdmins = async (): Promise<void> => {
    try {
      setAdminsLoading(true);
      const res = await api.get('/users/admins');
      const raw = res.data;
      const list = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.items)
          ? raw.items
          : Array.isArray(raw?.results)
            ? raw.results
            : [];
      setAdmins(list);
    } catch (e) {
      console.error('Load admins error:', e);
    } finally {
      setAdminsLoading(false);
    }
  };

  const loadCentralBuyers = async (): Promise<void> => {
    try {
      setLoadingCentrals(true);
      const res = await api.get('/clients/centrales');
      const centrals = Array.isArray(res.data) ? res.data : [];
      setCentralBuyers(centrals);
    } catch (e) {
      console.error('Load central buyers error:', e);
      setCentralBuyers([]);
    } finally {
      setLoadingCentrals(false);
    }
  };

  useEffect(() => {
    if (isOpen && (user?.role === 'super_admin' || user?.role === 'admin')) {
      if (admins.length === 0) void loadAdmins();
      if (user?.role === 'super_admin' && centralBuyers.length === 0) void loadCentralBuyers();
    }
  }, [isOpen, user?.role, admins.length, centralBuyers.length]);

  const handleSaveClick = (): void => {
    if (!client) return;
    // Validation
    if (!form.company_name.trim()) {
      showError('Le nom de l\'entreprise est obligatoire');
      return;
    }
    if (!form.email.trim()) {
      showError('L\'email est obligatoire');
      return;
    }
    if (form.client_type === 'member' && !form.parent_buyer_id) {
      showError('Un client membre doit être rattaché à une centrale d\'achat');
      return;
    }
    if (form.client_type !== 'member' && form.parent_buyer_id) {
      showError('Seul un client membre peut avoir une centrale parente');
      return;
    }

    // Show email action modal
    setShowEmailModal(true);
  };

  const handleEmailActionConfirm = async (action: 'welcome' | 'reset' | 'none'): Promise<void> => {
    await save(action);
  };

  const save = async (emailAction: 'welcome' | 'reset' | 'none'): Promise<void> => {
    if (!client) return;
    setSaving(true);
    try {
      const addressComplete = buildAddressComplete(form.address, form.postal_code, form.city);
      const isAdminUser = user?.role === 'super_admin' || user?.role === 'admin';
      const { admin_id, ...formWithoutAdmin } = form;
      const payload: any = {
        ...(isAdminUser ? form : formWithoutAdmin),
        maps_link: buildMapsLink(addressComplete),
        email_action: emailAction,
      };
      if (!payload.password) delete payload.password;
      if (!isAdminUser) {
        delete payload.admin_id;
        delete payload.admin;
        delete payload.administrateur;
      }
      if (payload.client_type === 'central') {
        payload.client_type = 'centrale';
      }
      const response = await api.patch(`/clients/${client.id}`, payload);
      const updatedClient: Client = response.data;
      onSuccess(updatedClient);
      onClose();
    } catch (e: any) {
      console.error('Save error:', e);
      const detail = e.response?.data?.detail;
      let errorMessage = 'Erreur lors de l\'enregistrement';
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        errorMessage = detail.map((err: any) => err.msg || err.message || JSON.stringify(err)).join(', ');
      }
      showError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={() => {
        if (!showEmailModal) onClose();
      }}>
        <DialogContent
          className="max-w-2xl w-full max-h-[90vh] overflow-y-auto p-0 border-none bg-transparent [&>*]:pointer-events-auto"
          onPointerDownOutside={(e) => {
            if (showEmailModal) e.preventDefault();
          }}
          onInteractOutside={(e) => {
            if (showEmailModal) e.preventDefault();
          }}
        >
          <VisuallyHidden>
            <DialogTitle>Modifier Client</DialogTitle>
          </VisuallyHidden>
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl overflow-hidden flex flex-col pointer-events-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 sticky top-0 z-10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
                  <Users className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">Modifier Client</h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Modifier les informations du client
                  </p>
                </div>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">✕</button>
            </div>

            {/* Stepper - Always visible */}
            <div className="px-6 pt-4">
              <div className="flex items-center justify-between">
                {[
                  { step: 1, label: 'Entreprise', icon: Building2 },
                  { step: 2, label: 'Contact', icon: Users },
                  ...(user?.role === 'admin' || user?.role === 'super_admin' ? [{ step: 3, label: 'Apporteur', icon: Shield }] : []),
                  ...(user?.role === 'super_admin' ? [{ step: 4, label: 'Admin', icon: Shield }] : []),
                  { step: user?.role === 'super_admin' ? 5 : (user?.role === 'admin' ? 4 : 3), label: 'Marque', icon: Tag },
                ].map((s, idx, arr) => (
                  <div key={s.step} className="flex items-center flex-1">
                    <div className="flex flex-col items-center flex-1">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all cursor-pointer ${formStep >= s.step
                          ? 'bg-purple-600 text-white'
                          : 'bg-slate-200 dark:bg-slate-700 text-slate-500'
                          }`}
                        onClick={() => {
                          setFormStep(s.step as any);
                        }}
                      >
                        {formStep > s.step ? '✓' : <s.icon className="h-4 w-4" />}
                      </div>
                      <span className={`text-xs mt-1.5 font-medium ${formStep >= s.step ? 'text-purple-600' : 'text-slate-400'}`}>
                        {s.label}
                      </span>
                    </div>
                    {idx < arr.length - 1 && (
                      <div className={`h-1 flex-1 mx-2 rounded ${formStep > s.step ? 'bg-purple-600' : 'bg-slate-200 dark:bg-slate-700'}`} />
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="p-6 space-y-4">
              {/* Step 1: Entreprise */}
              {formStep === 1 && (
                <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
                  <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    Informations Entreprise
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <label htmlFor="client_company_name" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Nom de l'entreprise *</label>
                      <input id="client_company_name" autoComplete="off" className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
                    </div>
                    <div>
                      <label htmlFor="client_type" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Type Client *</label>
                      <select
                        id="client_type"
                        className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        value={form.client_type}
                        onChange={(e) => setForm({ ...form, client_type: e.target.value as any })}
                      >
                        <option value="pharmacie">Pharmacie</option>
                        <option value="parapharmacie">Parapharmacie</option>
                        {user?.role === 'super_admin' && <option value="centrale">Centrale d'Achat</option>}
                        <option value="magasin_bio">Magasin Bio</option>
                        <option value="therapeute">Thérapeute</option>
                        <option value="member">Client Membre</option>
                        <option value="independent">Indépendant</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Rendez-vous</label>
                      <div className="flex items-center gap-4">
                        {(['R0', 'R1', 'R2', 'R3'] as const).map((rdv) => (
                          <label key={rdv} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="rdv_status"
                              value={rdv}
                              checked={form.rdv_status === rdv}
                              onChange={(e) => setForm({ ...form, rdv_status: e.target.value as any })}
                              className="w-4 h-4 accent-slate-800"
                            />
                            <span className="text-sm font-medium text-slate-800 dark:text-slate-200">{rdv}</span>
                          </label>
                        ))}
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, rdv_status: '' })}
                          className="text-xs text-slate-400 hover:text-slate-600"
                        >
                          Effacer
                        </button>
                      </div>
                    </div>
                    <div>
                      <label htmlFor="client_cote" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Cote</label>
                      <select
                        id="client_cote"
                        className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        value={form.cote}
                        onChange={(e) => setForm({ ...form, cote: e.target.value as any })}
                      >
                        <option value="">— Sélectionner —</option>
                        <option value="Glacier">🔵 Glacier</option>
                        <option value="Froid">🔴 Froid</option>
                        <option value="Tiède">🟡 Tiède</option>
                        <option value="Chaud">🟢 Chaud</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="client_siret" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">SIRET</label>
                      <input id="client_siret" autoComplete="off" className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500" value={form.siret} onChange={(e) => setForm({ ...form, siret: e.target.value })} placeholder="Ex: 123 456 789 00012" />
                    </div>
                    <div>
                      <label htmlFor="client_website" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">
                        <span className="flex items-center gap-1.5">
                          Site web
                          <ExternalLink className="h-3 w-3 text-slate-400" />
                        </span>
                      </label>
                      <div className="flex gap-2">
                        <input
                          id="client_website"
                          type="url"
                          className="flex-1 px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                          value={form.website}
                          onChange={(e) => setForm({ ...form, website: e.target.value })}
                          placeholder="https://www.exemple.com"
                        />
                        {form.website && (
                          <a
                            href={form.website}
                            target="_blank"
                            rel="noreferrer"
                            className="px-3 py-2 bg-blue-50 dark:bg-blue-950/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors flex items-center gap-1"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        )}
                      </div>
                    </div>
                    <div>
                      <label htmlFor="client_pappers_url" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">
                        <span className="flex items-center gap-1.5">
                          Pappers.fr
                          <ExternalLink className="h-3 w-3 text-slate-400" />
                        </span>
                      </label>
                      <div className="flex gap-2">
                        <input
                          id="client_pappers_url"
                          type="url"
                          className="flex-1 px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                          value={form.pappers_url}
                          onChange={(e) => setForm({ ...form, pappers_url: e.target.value })}
                          placeholder="https://www.pappers.fr/entreprise/..."
                        />
                        {form.pappers_url && (
                          <a
                            href={form.pappers_url}
                            target="_blank"
                            rel="noreferrer"
                            className="px-3 py-2 bg-blue-50 dark:bg-blue-950/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors flex items-center gap-1"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        )}
                      </div>
                    </div>
                    <div>
                      <label htmlFor="client_infogreffe_url" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">
                        <span className="flex items-center gap-1.5">
                          Infogreffe
                          <ExternalLink className="h-3 w-3 text-slate-400" />
                        </span>
                      </label>
                      <div className="flex gap-2">
                        <input
                          id="client_infogreffe_url"
                          type="url"
                          className="flex-1 px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                          value={form.infogreffe_url}
                          onChange={(e) => setForm({ ...form, infogreffe_url: e.target.value })}
                          placeholder="https://www.infogreffe.fr/entreprise/..."
                        />
                        {form.infogreffe_url && (
                          <a
                            href={form.infogreffe_url}
                            target="_blank"
                            rel="noreferrer"
                            className="px-3 py-2 bg-blue-50 dark:bg-blue-950/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors flex items-center gap-1"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        )}
                      </div>
                    </div>
                    <div>
                      <label htmlFor="client_pharmanity_url" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">
                        <span className="flex items-center gap-1.5">
                          Pharmanity
                          <ExternalLink className="h-3 w-3 text-slate-400" />
                        </span>
                      </label>
                      <div className="flex gap-2">
                        <input
                          id="client_pharmanity_url"
                          type="url"
                          className="flex-1 px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                          value={form.pharmanity_url}
                          onChange={(e) => setForm({ ...form, pharmanity_url: e.target.value })}
                          placeholder="https://www.pharmanity.fr/entreprise/..."
                        />
                        {form.pharmanity_url && (
                          <a
                            href={form.pharmanity_url}
                            target="_blank"
                            rel="noreferrer"
                            className="px-3 py-2 bg-blue-50 dark:bg-blue-950/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors flex items-center gap-1"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>

                  <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider flex items-center gap-2 pt-2">
                    <FileText className="h-4 w-4" />
                    Données de facturation
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <label htmlFor="client_invoice_email" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Email de facturation</label>
                      <input
                        id="client_invoice_email"
                        type="email"
                        autoComplete="off"
                        className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        value={form.invoice_email}
                        onChange={(e) => setForm({ ...form, invoice_email: e.target.value })}
                        placeholder="Email pour les factures (si différent de l'email principal)"
                      />
                    </div>
                    <div>
                      <label htmlFor="client_payment_mode" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Mode de règlement</label>
                      <select
                        id="client_payment_mode"
                        className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        value={form.payment_mode}
                        onChange={(e) => setForm({ ...form, payment_mode: e.target.value })}
                      >
                        <option value="Virement">Virement</option>
                        <option value="Prélèvement">Prélèvement</option>
                        {/* <option value="Carte bancaire">Carte bancaire</option>
                    <option value="Prélèvement">Prélèvement</option>
                    <option value="Chèque">Chèque</option>
                    <option value="Espèces">Espèces</option> */}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="client_payment_terms" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Terme de règlement</label>
                      <select
                        id="client_payment_terms"
                        className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        value={form.payment_terms}
                        onChange={(e) => setForm({ ...form, payment_terms: e.target.value })}
                      >
                        {paymentTermsList.length > 0 ? (
                          paymentTermsList.map(t => (
                            <option key={t.id} value={t.value}>{t.label}</option>
                          ))
                        ) : (
                          <>
                            <option value="À réception">Comptant</option>
                            <option value="30 jours Date Facture">30 jours Date Facture</option>
                            <option value="30 jours FDM">30 jours FDM</option>
                            <option value="45 jours FDM">45 jours FDM</option>
                            <option value="60 jours FDM">60 jours FDM</option>
                          </>
                        )}
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-3">Localisation</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="sm:col-span-2">
                            <label htmlFor="client_address" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Rue</label>
                            <input
                              id="client_address"
                              autoComplete="off"
                              className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                              value={form.address}
                              onChange={(e) =>
                                setForm((prev) => {
                                  const next = { ...prev, address: e.target.value };
                                  const addressComplete = buildAddressComplete(next.address, next.postal_code, next.city);
                                  return { ...next, maps_link: buildMapsLink(addressComplete) };
                                })
                              }
                              placeholder="Ex: 44 Avenue Gambetta"
                            />
                          </div>
                          <div>
                            <label htmlFor="client_postal_code" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Code postal</label>
                            <input
                              id="client_postal_code"
                              autoComplete="off"
                              className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                              value={form.postal_code}
                              onChange={(e) =>
                                setForm((prev) => {
                                  const next = { ...prev, postal_code: e.target.value };
                                  const addressComplete = buildAddressComplete(next.address, next.postal_code, next.city);
                                  return { ...next, maps_link: buildMapsLink(addressComplete) };
                                })
                              }
                              placeholder="Ex: 17100"
                            />
                          </div>
                          <div>
                            <label htmlFor="client_city" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Ville</label>
                            <input
                              id="client_city"
                              autoComplete="off"
                              className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                              value={form.city}
                              onChange={(e) =>
                                setForm((prev) => {
                                  const next = { ...prev, city: e.target.value };
                                  const addressComplete = buildAddressComplete(next.address, next.postal_code, next.city);
                                  return { ...next, maps_link: buildMapsLink(addressComplete) };
                                })
                              }
                              placeholder="Ex: Saintes"
                            />
                          </div>
                          <div className="sm:col-span-2">
                            <label htmlFor="client_address_complete" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Adresse complète</label>
                            <input
                              id="client_address_complete"
                              autoComplete="off"
                              className="w-full px-3 py-2 border rounded-lg bg-slate-50 dark:bg-slate-800/60 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100"
                              value={buildAddressComplete(form.address, form.postal_code, form.city)}
                              readOnly
                            />
                            {buildAddressComplete(form.address, form.postal_code, form.city) && (
                              <div className="mt-2 flex flex-wrap items-center gap-3">
                                <button
                                  type="button"
                                  onClick={() => setShowMapPreview((v) => !v)}
                                  className={
                                    "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors " +
                                    (showMapPreview
                                      ? 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/40 dark:bg-blue-950/40 dark:text-blue-300'
                                      : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700')
                                  }
                                >
                                  <Map className="h-3.5 w-3.5" />
                                  Carte
                                </button>
                                {form.maps_link && (
                                  <a
                                    href={form.maps_link}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-sm text-blue-600 hover:underline hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                                  >
                                    Ouvrir dans Google Maps
                                  </a>
                                )}
                              </div>
                            )}
                            {showMapPreview && (
                              <div className="mt-3 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                                <iframe
                                  title="Google Maps"
                                  src={buildMapsEmbedSrc(buildAddressComplete(form.address, form.postal_code, form.city))}
                                  className="w-full h-64"
                                  referrerPolicy="no-referrer-when-downgrade"
                                  allowFullScreen
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Compte Utilisateur */}
              {formStep === 2 && (
                <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
                  <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Contact Utilisateur
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="client_first_name" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Prénom</label>
                      <input
                        id="client_first_name"
                        type="text"
                        autoComplete="off"
                        className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        value={form.first_name}
                        onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                        placeholder="Ex : Marie"
                      />
                    </div>
                    <div>
                      <label htmlFor="client_last_name" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Nom</label>
                      <input
                        id="client_last_name"
                        type="text"
                        autoComplete="off"
                        className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        value={form.last_name}
                        onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                        placeholder="Ex : Dupont"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor="client_email" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Email *</label>
                      <input id="client_email" type="email" className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor="client_password" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Mot de passe</label>
                      <div className="relative">
                        <input id="client_password" type={showPassword ? 'text' : 'password'} autoComplete="new-password" className="w-full px-3 py-2 pr-10 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Laisser vide pour ne pas changer" />
                        <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Assignation Apporteur */}
              {formStep === 3 && (user?.role === 'admin' || user?.role === 'super_admin') && (
                <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
                  <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    Assignation Apporteur
                  </h3>
                  <div className="space-y-4">
                    <label htmlFor="client_admin_id" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Choisir l'apporteur d'affaires *</label>
                    <select
                      id="client_admin_id"
                      className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      value={form.admin_id || ''}
                      onChange={e => setForm({ ...form, admin_id: e.target.value ? parseInt(e.target.value) : null })}
                      disabled={adminsLoading}
                    >
                      <option value="">Sélectionner un apporteur...</option>
                      {admins.filter(a => (a as any).role === 'apporteur').map(a => <option key={a.id} value={a.id}>{a.first_name} {a.last_name} ({a.email})</option>)}
                    </select>
                    {!form.admin_id && <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-xs text-amber-800">⚠️ Aucun apporteur sélectionné. Le client sera assigné par défaut.</div>}

                    {user?.role === 'admin' && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                        <div>
                          <label htmlFor="client_status" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Statut Client</label>
                          <select
                            id="client_status"
                            className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            value={form.status}
                            onChange={(e) => setForm({ ...form, status: e.target.value as any })}
                          >
                            <option value="prospect">Prospect</option>
                            <option value="actif">Actif</option>
                            <option value="inactif">Inactif</option>
                            <option value="bloque">Bloqué</option>
                            <option value="ferme">Fermé</option>
                          </select>
                        </div>
                        <div>
                          <label htmlFor="client_is_active" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Accès Compte</label>
                          <select
                            id="client_is_active"
                            className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            value={form.is_active ? 'active' : 'inactive'}
                            onChange={(e) => setForm({ ...form, is_active: e.target.value === 'active' })}
                          >
                            <option value="active">Activé</option>
                            <option value="inactive">Désactivé</option>
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Step 4: Admin Options (super_admin only) */}
              {formStep === 4 && user?.role === 'super_admin' && (
                <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
                  <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    Options Administration
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="client_status" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Statut Client</label>
                      <select
                        id="client_status"
                        className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        value={form.status}
                        onChange={(e) => setForm({ ...form, status: e.target.value as any })}
                      >
                        <option value="prospect">Prospect</option>
                        <option value="actif">Actif</option>
                        <option value="inactif">Inactif</option>
                        <option value="bloque">Bloqué</option>
                        <option value="ferme">Fermé</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="client_is_active" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Accès Compte</label>
                      <select
                        id="client_is_active"
                        className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        value={form.is_active ? 'active' : 'inactive'}
                        onChange={(e) => setForm({ ...form, is_active: e.target.value === 'active' })}
                      >
                        <option value="active">Activé</option>
                        <option value="inactive">Désactivé</option>
                      </select>
                    </div>

                    {form.client_type === 'member' && (
                      <div className="sm:col-span-2">
                        <label htmlFor="client_parent_buyer_id" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Centrale d'achat parente *</label>
                        <select
                          id="client_parent_buyer_id"
                          className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          value={form.parent_buyer_id || ''}
                          onChange={(e) => setForm({ ...form, parent_buyer_id: e.target.value ? parseInt(e.target.value, 10) : null })}
                          onFocus={() => {
                            if (centralBuyers.length === 0 && !loadingCentrals) {
                              loadCentralBuyers();
                            }
                          }}
                          disabled={loadingCentrals}
                        >
                          {loadingCentrals ? (
                            <option disabled>Chargement...</option>
                          ) : (
                            <option value="">Sélectionner une centrale...</option>
                          )}
                          {!loadingCentrals && centralBuyers.map(central => (
                            <option key={central.id} value={central.id}>
                              {central.company_name}
                            </option>
                          ))}
                        </select>
                        {form.parent_buyer_id && (
                          <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                            <p className="text-sm text-blue-800 dark:text-blue-300">
                              ℹ️ Le BPU sera hérité de la centrale sélectionnée.
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {form.client_type !== 'centrale' && (
                      <div className="sm:col-span-2 pt-4 mt-2 border-t border-slate-100 dark:border-slate-800">
                        <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-3 flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-purple-500" />
                          Rattachement Centrale d'Achat
                        </h4>
                        <div className="space-y-4">
                          <div>
                            <label htmlFor="client_central_purchasing_id" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Centrale de rattachement (Optionnel)</label>
                            <select
                              id="client_central_purchasing_id"
                              className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                              value={form.central_purchasing_id || ''}
                              onChange={e => setForm({ ...form, central_purchasing_id: e.target.value ? parseInt(e.target.value) : null })}
                              onFocus={() => !centralBuyers.length && loadCentralBuyers()}
                              disabled={loadingCentrals}
                            >
                              <option value="">Aucune centrale rattachée</option>
                              {centralBuyers.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                            </select>
                          </div>

                          {form.central_purchasing_id && (
                            <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-lg border border-slate-200 dark:border-slate-700 animate-in fade-in slide-in-from-top-2">
                              <label className="block text-sm font-medium mb-3 text-slate-900 dark:text-slate-100">Choix du BPU à appliquer :</label>
                              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                                <label className="flex items-center gap-2 cursor-pointer group">
                                  <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-colors pointer-events-none ${form.use_central_purchasing_bpu ? 'border-purple-600 bg-purple-600' : 'border-slate-400 bg-white dark:bg-slate-700'}`}>
                                    {form.use_central_purchasing_bpu && <div className="w-2 h-2 rounded-full bg-white" />}
                                  </div>
                                  <input
                                    type="radio"
                                    name="bpu_mode"
                                    checked={form.use_central_purchasing_bpu === true}
                                    onChange={() => setForm({ ...form, use_central_purchasing_bpu: true })}
                                    className="sr-only"
                                  />
                                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300 group-hover:text-purple-600 transition-colors pointer-events-none">Utiliser le BPU de la centrale</span>
                                </label>

                                <label className="flex items-center gap-2 cursor-pointer group">
                                  <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-colors pointer-events-none ${!form.use_central_purchasing_bpu ? 'border-purple-600 bg-purple-600' : 'border-slate-400 bg-white dark:bg-slate-700'}`}>
                                    {!form.use_central_purchasing_bpu && <div className="w-2 h-2 rounded-full bg-white" />}
                                  </div>
                                  <input
                                    type="radio"
                                    name="bpu_mode"
                                    checked={form.use_central_purchasing_bpu === false}
                                    onChange={() => setForm({ ...form, use_central_purchasing_bpu: false })}
                                    className="sr-only"
                                  />
                                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300 group-hover:text-purple-600 transition-colors pointer-events-none">Utiliser le BPU propre du client</span>
                                </label>
                              </div>
                              <p className="text-xs text-slate-500 dark:text-slate-400 mt-2 pl-6 border-l-2 border-slate-300 dark:border-slate-600 italic">
                                {form.use_central_purchasing_bpu
                                  ? "Les modifications du BPU de la centrale seront automatiquement répercutées sur ce client."
                                  : "Le client utilise exclusivement son propre BPU. Les changements de la centrale n'auront aucun impact."}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Step 5: Marque */}
              {((user?.role === 'super_admin' && formStep === 5) || (user?.role === 'admin' && formStep === 4) || (user?.role === 'apporteur' && formStep === 3)) && (
                <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
                  <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider flex items-center gap-2">
                    <Tag className="h-4 w-4" />
                    Marque
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <label htmlFor="client_marque" className="block text-sm font-medium mb-1 text-slate-700 dark:text-slate-300">Marque</label>
                      <select
                        id="client_marque"
                        className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        value={form.marque}
                        onChange={e => setForm({ ...form, marque: e.target.value })}
                      >
                        <option value="">Sélectionner une marque</option>
                        {['NOVA FLORA', 'DOUCE FRANCE'].map((b) => (
                          <option key={b} value={b}>{b}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Navigation */}
            <div className="flex justify-between gap-2 pt-4 border-t border-slate-200 dark:border-slate-700 p-6">
              <button
                onClick={() => {
                  if (formStep === 1) {
                    onClose();
                  } else {
                    setFormStep((formStep - 1) as any);
                  }
                }}
                className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              >
                {formStep === 1 ? 'Annuler' : 'Précédent'}
              </button>

              <button
                onClick={async () => {
                  const maxStep = user?.role === 'super_admin' ? 5 : (user?.role === 'admin' ? 4 : 3);
                  if (formStep < maxStep) {
                    setFormStep((formStep + 1) as any);
                  } else {
                    handleSaveClick();
                  }
                }}
                disabled={!form.company_name.trim() || !form.email.trim() || saving}
                className="px-6 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors font-medium flex items-center gap-2"
              >
                {saving && (
                  <Loader className="animate-spin h-4 w-4" />
                )}
                {formStep < (user?.role === 'super_admin' ? 5 : (user?.role === 'admin' ? 4 : 3))
                  ? 'Suivant'
                  : (saving ? 'Enregistrement...' : 'Enregistrer')
                }
              </button>
            </div>
          </div>
        </DialogContent>

        <EmailActionModal
          isOpen={showEmailModal}
          onClose={() => setShowEmailModal(false)}
          onConfirm={handleEmailActionConfirm}
          isNewClient={false}
          clientName={form.company_name || `${form.first_name} ${form.last_name}`.trim() || form.email}
        />
      </Dialog>
    </>
  );
};
